/*
 * "Trafico": KPIs, dos graficas y un ranking de entradas por tienda, alimentados directo desde el
 * proyecto Supabase "orocash-conteo" (config/supabaseTrafico.js) - Django solo dice que device_id
 * le tocan ver a este usuario (GET /igi/trafico/dispositivos/, filtrado por su territorio IGI).
 * Mismo patron pedido/cargando que el resto de igi (pulso/index.js, mi_zona/index.js).
 */
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Chart} from "react-google-charts";
import {useDispatch, useSelector} from "react-redux";
import Breadcrumbs from "../../../../components/breadcrumbs";
import {
    clear_igi,
    get_trafico_chart,
    get_trafico_dispositivos,
    get_trafico_hourly,
    get_trafico_ranking,
    GET_TRAFICO_DISPOSITIVOS_SUCCESS,
} from "../../../../redux/igi";

const MODOS = [
    ["hoy", "Hoy"],
    ["7d", "7 días"],
    ["30d", "30 días"],
    ["90d", "90 días"],
    ["custom", "Rango personalizado"],
];

const dosDigitos = (n) => String(n).padStart(2, "0");

const fechasPara = (modo, rango) => {
    const hoy = new Date();
    const fmt = (d) => `${d.getFullYear()}-${dosDigitos(d.getMonth() + 1)}-${dosDigitos(d.getDate())}`;
    const hoyStr = fmt(hoy);
    if (modo === "hoy") return {from: hoyStr, to: hoyStr};
    if (modo === "7d") { const d = new Date(hoy); d.setDate(d.getDate() - 6); return {from: fmt(d), to: hoyStr}; }
    if (modo === "30d") { const d = new Date(hoy); d.setDate(d.getDate() - 29); return {from: fmt(d), to: hoyStr}; }
    if (modo === "90d") { const d = new Date(hoy); d.setDate(d.getDate() - 89); return {from: fmt(d), to: hoyStr}; }
    return {from: rango.from, to: rango.to};
};

const calcularKPIs = (chart, hourly) => {
    const total = chart.reduce((a, r) => a + (r.total_in || 0), 0);
    const dias = chart.filter((r) => (r.total_in || 0) > 0).length || 1;
    let horaPico = "—", picoHoraValor = 0;
    hourly.forEach((r) => {
        if ((r.total_in || 0) > picoHoraValor) { picoHoraValor = r.total_in; horaPico = r.hour_label; }
    });
    let diaPico = "—", picoDiaValor = 0;
    chart.forEach((r) => {
        if ((r.total_in || 0) > picoDiaValor) { picoDiaValor = r.total_in; diaPico = r.label; }
    });
    const promedioPico = picoHoraValor > 0 ? Math.round(picoHoraValor / dias) : 0;
    return {
        totalEntradas: total,
        promedioDia: Math.round(total / dias),
        horaPico,
        promedioPico,
        diaPico: diaPico !== "—"
            ? `${diaPico.slice(5)}${picoDiaValor > 0 ? ` · ${picoDiaValor.toLocaleString("es-EC")}` : ""}`
            : "—",
    };
};

const calcularRanking = (data, mapaDispositivos) => {
    const porTienda = {};
    data.forEach((r) => {
        const mapeo = mapaDispositivos[r.device_id];
        const clave = mapeo ? mapeo.tiendaId : (r.store_name || r.device_id);
        const nombre = mapeo ? mapeo.tienda : (r.store_name || r.device_id);
        if (!porTienda[clave]) porTienda[clave] = {nombre, total: 0};
        porTienda[clave].total += r.total_in || 0;
    });
    const filas = Object.values(porTienda).sort((a, b) => b.total - a.total);
    const total = filas.reduce((a, r) => a + r.total, 0) || 1;
    return filas.map((r) => ({...r, porcentaje: ((r.total / total) * 100).toFixed(1)}));
};

const Trafico = () => {
    const dispatch = useDispatch();
    const {token} = useSelector((state) => state.users_module.login.data) || null;
    // Los || de abajo son necesarios: un igi_module persistido de antes de que existieran estas
    // 4 claves (redux-persist reemplaza el slice completo al rehidratar, sin fusionar por clave)
    // llega sin ellas -> undefined.data tumbaba toda la pantalla la primera vez que alguien con
    // sesion vieja entraba despues de este deploy.
    const dispositivos = useSelector((state) => state.igi_module._get_trafico_dispositivos)
        || {data: {}, status: 0, message: {}};
    const ranking = useSelector((state) => state.igi_module._get_trafico_ranking)
        || {data: [], status: 0, message: {}};
    const chart = useSelector((state) => state.igi_module._get_trafico_chart)
        || {data: [], status: 0, message: {}};
    const hourly = useSelector((state) => state.igi_module._get_trafico_hourly)
        || {data: [], status: 0, message: {}};

    const [pedidoDispositivos, setPedidoDispositivos] = useState(false);
    const [pedidoDatos, setPedidoDatos] = useState(false);
    const [modo, setModo] = useState("7d");
    const [rango, setRango] = useState({from: "", to: ""});
    // "" = todo lo visible; "zona:<id>" = una zona completa; "tienda:<id>" = una tienda puntual.
    const [seleccion, setSeleccion] = useState("");

    useEffect(() => {
        dispatch(clear_igi(GET_TRAFICO_DISPOSITIVOS_SUCCESS));
        setPedidoDispositivos(true);
        dispatch(get_trafico_dispositivos(token));
    }, [dispatch, token]);

    const listaDispositivos = useMemo(
        () => (dispositivos.data && dispositivos.data.datos ? dispositivos.data.datos : []),
        [dispositivos.data],
    );

    const tiendas = useMemo(() => {
        const porTienda = {};
        listaDispositivos.forEach((d) => {
            if (!porTienda[d.tienda_id]) {
                porTienda[d.tienda_id] = {id: d.tienda_id, nombre: d.tienda, zonaId: d.zona_id, deviceIds: []};
            }
            porTienda[d.tienda_id].deviceIds.push(d.device_id);
        });
        return Object.values(porTienda).sort((a, b) => a.nombre.localeCompare(b.nombre, "es"));
    }, [listaDispositivos]);

    const zonas = useMemo(() => {
        const porZona = {};
        listaDispositivos.forEach((d) => {
            if (!porZona[d.zona_id]) porZona[d.zona_id] = {id: d.zona_id, nombre: d.zona, deviceIds: []};
            porZona[d.zona_id].deviceIds.push(d.device_id);
        });
        return Object.values(porZona)
            .sort((a, b) => (a.nombre || "").localeCompare(b.nombre || "", "es"))
            .map((z) => ({...z, tiendas: tiendas.filter((t) => String(t.zonaId) === String(z.id))}));
    }, [listaDispositivos, tiendas]);

    const mapaDispositivos = useMemo(() => {
        const mapa = {};
        listaDispositivos.forEach((d) => { mapa[d.device_id] = {tiendaId: d.tienda_id, tienda: d.tienda}; });
        return mapa;
    }, [listaDispositivos]);

    let deviceIdsActivos = listaDispositivos.map((d) => d.device_id);
    if (seleccion.startsWith("zona:")) {
        const zonaId = seleccion.slice(5);
        deviceIdsActivos = (zonas.find((z) => String(z.id) === zonaId) || {deviceIds: []}).deviceIds;
    } else if (seleccion.startsWith("tienda:")) {
        const tiendaId = seleccion.slice(7);
        deviceIdsActivos = (tiendas.find((t) => String(t.id) === tiendaId) || {deviceIds: []}).deviceIds;
    }

    const {from, to} = fechasPara(modo, rango);
    const fechasCompletas = modo !== "custom" || (rango.from && rango.to && rango.from <= rango.to);
    const claveActual = `${from}|${to}|${deviceIdsActivos.join(",")}`;

    const recargarDatos = useCallback(() => {
        if (!deviceIdsActivos.length || !fechasCompletas) return;
        setPedidoDatos(true);
        dispatch(get_trafico_ranking(from, to, deviceIdsActivos, claveActual));
        dispatch(get_trafico_chart(from, to, deviceIdsActivos, claveActual));
        dispatch(get_trafico_hourly(from, to, deviceIdsActivos, claveActual));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, from, to, deviceIdsActivos.join(","), fechasCompletas, claveActual]);

    useEffect(() => {
        if (dispositivos.status === 200) recargarDatos();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispositivos.status, recargarDatos]);

    const vigente = (slice) => (slice.clave === claveActual ? slice : {data: [], status: 0, message: ""});
    const rankingVigente = vigente(ranking);
    const chartVigente = vigente(chart);
    const hourlyVigente = vigente(hourly);

    const cargandoDispositivos = pedidoDispositivos && dispositivos.status === 0;
    const cargandoDatos = pedidoDatos
        && (rankingVigente.status === 0 || chartVigente.status === 0 || hourlyVigente.status === 0);
    const errorDispositivos = dispositivos.status !== 0 && dispositivos.status !== 200
        ? (dispositivos.message || "No se pudo cargar el territorio") : null;
    const errorDatos = [rankingVigente, chartVigente, hourlyVigente].some((r) => r.status === 500)
        ? "No se pudo cargar el tráfico, intenta de nuevo." : null;

    const sinCamaras = dispositivos.status === 200 && listaDispositivos.length === 0;
    const sinCamarasEnSeleccion = dispositivos.status === 200 && listaDispositivos.length > 0
        && seleccion !== "" && deviceIdsActivos.length === 0;

    const datosChart = chartVigente.data || [];
    const datosHourly = hourlyVigente.data || [];
    const kpis = calcularKPIs(datosChart, datosHourly);
    const filasRanking = calcularRanking(rankingVigente.data || [], mapaDispositivos);

    const datosBarraDias = [
        ["Día", "Entradas"],
        ...datosChart.map((r) => [(r.label || "").slice(5), r.total_in || 0]),
    ];
    const datosBarraHoras = [
        ["Hora", "Entradas"],
        ...Array.from({length: 24}, (_, i) => {
            const etiqueta = `${dosDigitos(i)}:00`;
            const fila = datosHourly.find((h) => h.hour_label === etiqueta);
            return [etiqueta, fila ? fila.total_in : 0];
        }),
    ];

    return (
        <div className="app-content content">
            <div className="content-overlay"/>
            <div className="content-wrapper">
                <div className="content-header row">
                    <div className="content-header-left col-12 mb-2 mt-1">
                        <Breadcrumbs title="Tráfico" items={[]}/>
                    </div>
                </div>
                <div className="content-body">
                    {cargandoDispositivos && (
                        <div className="alert alert-info" role="status" aria-busy="true">
                            <i className="bx bx-loader-alt bx-spin mr-50"/> Cargando territorio…
                        </div>
                    )}
                    {errorDispositivos && <div className="alert alert-danger">{errorDispositivos}</div>}

                    {dispositivos.status === 200 && (
                        <div className="card mb-1">
                            <div className="card-body d-flex flex-wrap align-items-center" style={{gap: "0.5rem"}}>
                                {MODOS.map(([valor, etiqueta]) => (
                                    <button
                                        key={valor}
                                        type="button"
                                        className={`btn btn-sm ${modo === valor ? "btn-primary" : "btn-outline-primary"}`}
                                        onClick={() => setModo(valor)}
                                    >
                                        {etiqueta}
                                    </button>
                                ))}
                                {modo === "custom" && (
                                    <>
                                        <input
                                            type="date"
                                            className="form-control form-control-sm"
                                            style={{maxWidth: "160px"}}
                                            value={rango.from}
                                            onChange={(e) => setRango((r) => ({...r, from: e.target.value}))}
                                        />
                                        <input
                                            type="date"
                                            className="form-control form-control-sm"
                                            style={{maxWidth: "160px"}}
                                            value={rango.to}
                                            onChange={(e) => setRango((r) => ({...r, to: e.target.value}))}
                                        />
                                    </>
                                )}
                                <select
                                    className="form-control form-control-sm"
                                    style={{maxWidth: "260px"}}
                                    value={seleccion}
                                    onChange={(e) => setSeleccion(e.target.value)}
                                >
                                    <option value="">Todas las tiendas</option>
                                    {zonas.map((z) => (
                                        <optgroup key={z.id} label={z.nombre || `Zona ${z.id}`}>
                                            <option value={`zona:${z.id}`}>Toda la zona</option>
                                            {z.tiendas.map((t) => (
                                                <option key={t.id} value={`tienda:${t.id}`}>{t.nombre}</option>
                                            ))}
                                        </optgroup>
                                    ))}
                                </select>
                            </div>
                        </div>
                    )}

                    {sinCamaras && (
                        <div className="alert alert-info">No hay cámaras configuradas para tu zona.</div>
                    )}
                    {sinCamarasEnSeleccion && (
                        <div className="alert alert-info">No hay cámaras configuradas para esta selección.</div>
                    )}
                    {!sinCamaras && !sinCamarasEnSeleccion && modo === "custom" && !fechasCompletas && (
                        <div className="alert alert-info">Elegí las dos fechas del rango para consultar.</div>
                    )}
                    {cargandoDatos && (
                        <div className="alert alert-info" role="status" aria-busy="true">
                            <i className="bx bx-loader-alt bx-spin mr-50"/> Consultando tráfico…
                        </div>
                    )}
                    {errorDatos && <div className="alert alert-danger">{errorDatos}</div>}

                    {!sinCamaras && !sinCamarasEnSeleccion && !errorDatos && !cargandoDatos && fechasCompletas && (
                        <>
                            <div className="row">
                                <div className="col-md-3 mb-1">
                                    <div className="card h-100"><div className="card-body">
                                        <div className="text-muted" style={{fontSize: "12px"}}>Total entradas</div>
                                        <div style={{fontSize: "22px", fontWeight: 600}}>
                                            {kpis.totalEntradas.toLocaleString("es-EC")}
                                        </div>
                                    </div></div>
                                </div>
                                <div className="col-md-3 mb-1">
                                    <div className="card h-100"><div className="card-body">
                                        <div className="text-muted" style={{fontSize: "12px"}}>Promedio / día</div>
                                        <div style={{fontSize: "22px", fontWeight: 600}}>
                                            {kpis.promedioDia.toLocaleString("es-EC")}
                                        </div>
                                    </div></div>
                                </div>
                                <div className="col-md-3 mb-1">
                                    <div className="card h-100"><div className="card-body">
                                        <div className="text-muted" style={{fontSize: "12px"}}>Hora más concurrida</div>
                                        <div style={{fontSize: "18px", fontWeight: 600}}>{kpis.horaPico}</div>
                                        {kpis.promedioPico > 0 && (
                                            <div className="text-muted" style={{fontSize: "11px"}}>
                                                ≈{kpis.promedioPico} personas/día
                                            </div>
                                        )}
                                    </div></div>
                                </div>
                                <div className="col-md-3 mb-1">
                                    <div className="card h-100"><div className="card-body">
                                        <div className="text-muted" style={{fontSize: "12px"}}>Día más activo</div>
                                        <div style={{fontSize: "16px", fontWeight: 600}}>{kpis.diaPico}</div>
                                    </div></div>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-6 mb-1">
                                    <div className="card"><div className="card-body">
                                        <div style={{fontSize: "12px", fontWeight: 500}} className="mb-1">
                                            Entradas por día
                                        </div>
                                        {datosChart.length === 0 && (
                                            <div className="text-muted text-center py-2">
                                                Sin datos para este período.
                                            </div>
                                        )}
                                        {datosChart.length > 0 && (
                                            <Chart
                                                chartType="ColumnChart"
                                                data={datosBarraDias}
                                                width="100%"
                                                height="260px"
                                                options={{legend: {position: "none"}}}
                                            />
                                        )}
                                    </div></div>
                                </div>
                                <div className="col-md-6 mb-1">
                                    <div className="card"><div className="card-body">
                                        <div style={{fontSize: "12px", fontWeight: 500}} className="mb-1">
                                            Distribución por hora
                                        </div>
                                        <Chart
                                            chartType="ColumnChart"
                                            data={datosBarraHoras}
                                            width="100%"
                                            height="260px"
                                            options={{legend: {position: "none"}}}
                                        />
                                    </div></div>
                                </div>
                            </div>

                            <div className="card">
                                <div className="card-body">
                                    <div style={{fontSize: "12px", fontWeight: 500}} className="mb-1">
                                        Ranking de entradas
                                    </div>
                                    {filasRanking.length === 0 && (
                                        <div className="text-muted text-center py-2">
                                            Sin datos para este período.
                                        </div>
                                    )}
                                    {filasRanking.length > 0 && (
                                        <div className="table-responsive">
                                            <table className="table table-hover">
                                                <thead>
                                                    <tr>
                                                        <th>#</th>
                                                        <th>Joyería</th>
                                                        <th className="text-right">Entradas</th>
                                                        <th className="text-right">% total</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {filasRanking.map((r, i) => (
                                                        <tr key={r.nombre}>
                                                            <td className="text-muted">{i + 1}</td>
                                                            <td><b>{r.nombre}</b></td>
                                                            <td className="text-right">
                                                                {r.total.toLocaleString("es-EC")}
                                                            </td>
                                                            <td className="text-right text-muted">
                                                                {r.porcentaje}%
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Trafico;
